import { SelectTescomaPointPopupProps } from 'components/Blocks/SelectPickupPointPopup/SelectTescomaPointPopup';
import Button from 'components/Forms/Button/Button';
import { useSettings } from 'connectors/Settings/Settings';
import { getLastDayFromPersonalPickupString } from 'helpers/getLastDayFromPersonalPickupString';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { RefObject, useCallback, useEffect } from 'react';
import * as smoothscroll from 'smoothscroll-polyfill';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { twJoin } from 'tailwind-merge';
import { AvailabilityStatusEnum, StoreListAvailabilityStatusEnum } from 'types/availability';
import { ProductDetailType, VariantDetailType } from 'types/product';
import { SearchedStoreType } from 'types/store';

const SelectTescomaPointPopup = dynamic<SelectTescomaPointPopupProps>(() =>
    import('components/Blocks/SelectPickupPointPopup/SelectTescomaPointPopup').then(
        (component) => component.SelectTescomaPointPopup,
    ),
);

type ProductDetailAvailabilityProps = {
    product: ProductDetailType | VariantDetailType;
    scrollTarget: RefObject<HTMLUListElement>;
};

const changeDefaultStoreTwClass = 'cursor-pointer text-black underline';

const ProductDetailAvailability: FC<ProductDetailAvailabilityProps> = ({ product }) => {
    const testIdentifier = 'pages-productdetail-productdetailavailability';
    const { t } = useTranslation();
    const settings = useSettings();
    const updatePortalContent = useSessionStore((s) => s.updatePortalContent);

    const { updateDefaultStoreUuidState, updateUserState } = usePersistStore((s) => s);
    const defaultStoreAvailability = product.defaultStoreAvailability;
    const isElectronicGiftVoucher = product.isElectronicGiftVoucher;
    const closePopupHandler = useCallback(() => updatePortalContent(null), [updatePortalContent]);
    const changeStoreHandler = useCallback(
        (store: SearchedStoreType | null) => {
            if (store !== null) {
                updateDefaultStoreUuidState(store.uuid);
                updateUserState({ selectedStoreUuid: store.uuid });
            }

            closePopupHandler();
        },
        [updateDefaultStoreUuidState, updateUserState, closePopupHandler],
    );

    const openPopupHandler = useCallback(
        () =>
            updatePortalContent(
                <SelectTescomaPointPopup
                    placement="product-detail"
                    popupTitle={t('Choose your Tescoma store')}
                    productDetail={product}
                    renderChangePickupPointButton={(store) => (
                        <Button
                            isDisabled={false}
                            size="small"
                            type="button"
                            variant="primary"
                            onClick={() => changeStoreHandler(store as SearchedStoreType)}
                        >
                            {t('Choose store')}
                        </Button>
                    )}
                />,
            ),
        [updatePortalContent, product, t, changeStoreHandler],
    );

    useEffect(() => {
        smoothscroll.polyfill();
    }, []);

    const isProductInStock = product.availability.status === AvailabilityStatusEnum.inStock;
    const isInCentralStoreWithEnabledReservations = product.deliveryDaysFromCentralStore !== null && isProductInStock;
    const isInCentralStoreWithEnabledReservationsAndNowhereElse =
        isInCentralStoreWithEnabledReservations && product.availableStoresCount === 0;
    const count = product.availableStoresCount;
    const { days, daysCount } = getLastDayFromPersonalPickupString(product.deliveryDaysFromCentralStore);

    const getStoreAvailabilityForDefaultStore = (
        inStock: boolean,
        inCentralStore: boolean,
        storeName: string,
        storesCount: number,
        deliveryDays: number,
    ) => {
        if (inStock) {
            return (
                <>
                    {`${t('Available')} `}
                    <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                        {t('in store {{ store }}', { store: storeName })}
                    </span>
                </>
            );
        }

        if (inCentralStore) {
            return (
                <>
                    {t('Personal pickup in {{ days }} days', { days, count: deliveryDays })}
                    <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                        &nbsp;
                        {t('in store {{ store }}', { store: storeName })}
                    </span>
                </>
            );
        }

        return (
            <span className="text-grayDark">
                <Trans
                    components={{ link: <span className={changeDefaultStoreTwClass} onClick={openPopupHandler} /> }}
                    i18nKey="Unavailable in store <link>{{ store }}</link>"
                    values={{ store: storeName }}
                />
            </span>
        );
    };

    const productDetailAvailabilityInfoTwClass = twJoin(
        'text-small font-bold leading-6',
        product.availableStoresCount > 0 ||
            defaultStoreAvailability?.availabilityStatus === StoreListAvailabilityStatusEnum.inStock
            ? 'text-green'
            : 'text-grayDark',
    );

    return (
        <div className="mb-5" data-testid={testIdentifier}>
            <div
                className={twJoin(
                    'text-h6 font-bold leading-6 hover:no-underline',
                    isProductInStock && 'text-green',
                    product.availability.status === AvailabilityStatusEnum.outOfStock && 'text-grayDark',
                )}
            >
                {isProductInStock ? t('Available in eshop') : t('Unavailable in eshop')}
            </div>
            {defaultStoreAvailability === null && !isElectronicGiftVoucher && (
                <div
                    className={productDetailAvailabilityInfoTwClass}
                    data-testid={testIdentifier + '-in-stock-at-stores'}
                >
                    {isInCentralStoreWithEnabledReservationsAndNowhereElse ? (
                        <>
                            {t('Available in')}{' '}
                            <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                                {t('(in) stores')}
                            </span>{' '}
                            {t('in {{ count }} days', { count: daysCount, days })}
                        </>
                    ) : (
                        <>
                            {count === 0 && settings?.isAnyStockStore && t('Unavailable in stores')}
                            {count !== 0 && `${t('Available')} `}
                            {count !== 0 && (
                                <span className={changeDefaultStoreTwClass} onClick={openPopupHandler}>
                                    {t('in {{ count }} stores', { count })}
                                </span>
                            )}
                        </>
                    )}
                </div>
            )}

            {defaultStoreAvailability !== null && (
                <div className={productDetailAvailabilityInfoTwClass} data-testid={testIdentifier + '-default-store'}>
                    {getStoreAvailabilityForDefaultStore(
                        [
                            StoreListAvailabilityStatusEnum.inStock,
                            StoreListAvailabilityStatusEnum.inStockReservationDisabled,
                        ].includes(defaultStoreAvailability.availabilityStatus),
                        isInCentralStoreWithEnabledReservations,
                        defaultStoreAvailability.storeName,
                        product.availableStoresCount,
                        daysCount,
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductDetailAvailability;
